import { z } from 'zod';

import type { StaffProfile, UpdateMeRequest } from '@nucleus-commerce/api-client/admin';

/** Backend rules for staff passwords and profile fields. */
const PASSWORD_MIN = 12;
const PASSWORD_MAX = 200;
const LOCALE = /^[a-z]{2}(?:-[A-Z]{2})?$/;

export const profileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'account:validation.required' })
    .max(120, { error: 'account:validation.nameLength' }),
  locale: z
    .string()
    .trim()
    .refine((v) => v === '' || LOCALE.test(v), { error: 'account:validation.locale' }),
});
export type ProfileValues = z.infer<typeof profileSchema>;

export function fromProfile(me: StaffProfile): ProfileValues {
  return { name: me.user.name, locale: me.user.locale ?? '' };
}

/** A blank locale goes back to following the browser. */
export function toProfileUpdate(values: ProfileValues): UpdateMeRequest {
  return { name: values.name, locale: values.locale === '' ? null : values.locale };
}

export const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, { error: 'account:validation.required' }).max(PASSWORD_MAX),
    newPassword: z
      .string()
      .min(PASSWORD_MIN, { error: 'account:validation.passwordLength' })
      .max(PASSWORD_MAX, { error: 'account:validation.passwordLength' }),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    error: 'account:validation.passwordMismatch',
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    error: 'account:validation.passwordSame',
  });
export type PasswordValues = z.infer<typeof passwordSchema>;

export const emailChangeSchema = z.object({
  newEmail: z.email({ error: 'account:validation.email' }),
  currentPassword: z.string().min(1, { error: 'account:validation.required' }).max(PASSWORD_MAX),
});
export type EmailChangeValues = z.infer<typeof emailChangeSchema>;

/** "Mozilla/5.0 (iPhone) Safari/18" → "Safari on iPhone"; unknown agents keep a generic label. */
export function describeDevice(userAgent: string | null): {
  browser: string | null;
  os: string | null;
} {
  if (!userAgent) return { browser: null, os: null };
  const browser = userAgent.includes('Edg/')
    ? 'Edge'
    : userAgent.includes('Firefox/')
      ? 'Firefox'
      : userAgent.includes('Chrome/')
        ? 'Chrome'
        : userAgent.includes('Safari/')
          ? 'Safari'
          : null;
  const os = /iPhone|iPad/.test(userAgent)
    ? 'iOS'
    : userAgent.includes('Android')
      ? 'Android'
      : /Macintosh|Mac OS/.test(userAgent)
        ? 'macOS'
        : userAgent.includes('Windows')
          ? 'Windows'
          : userAgent.includes('Linux')
            ? 'Linux'
            : null;
  return { browser, os };
}
